import {
  Component,
  OnInit,
  OnDestroy,
  Injector,
  Input
} from '@angular/core';
import { of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AdvertisementGraphqlService } from '../advertisement-graphql.service';
import { analyticsConstants, constants } from 'src/app/constants';
import { readCookie } from 'src/app/util';
import jwtDecode from 'jwt-decode';

import * as fromRoot from '../../../reducers';
import * as tracking from '../../../actions/tracking';
import { TealiumService } from '../../core/services/tealium.service';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';

interface AdvertisementData {
  mainText?: string;
  mainUrl?: string;
  mainImage?: string;
  secondaryImage?: string;
  mobileImage?: string;
  adLabel?: string;
  actionId: string;
  campaignDescription?: string;
}

interface VdsIdCookie {
  vdsId: string;
}
interface Payload {
  interaction: string;
  currentPage: string;
}

@Component({
    selector: 'app-advertisement',
    templateUrl: './advertisement.component.html',
    styleUrls: ['./advertisement.component.scss'],
    standalone: false
})
export class AdvertisementComponent
  implements OnInit, OnDestroy
{
  private readonly subscriptions: Subscription[] = [];
  private tealiumService: TealiumService;
  private featureFlagService: FeatureFlagsService;

  private static readonly ADS_SET_SAIL_PASS_ENABLED = 'adsSetSailPassEnabled';
  public showAdsLDFlag: boolean; // LaunchDarkly flag that determines show or not ads

  advertisementData: AdvertisementData;
  hasError = false;
  isLoading = true;

  @Input() isDesktop: boolean;
  @Input() advertisementPageId: string;

  constructor(
    private readonly advertisementService: AdvertisementGraphqlService,
    private readonly store: Store<fromRoot.AppState>,
    private readonly injector: Injector
  ) {
    this.injectServices();
  }

  injectServices(): void {
    this.tealiumService = this.injector.get<TealiumService>(TealiumService);
    this.featureFlagService = this.injector.get<FeatureFlagsService>(FeatureFlagsService);
  }

  ngOnInit(): void {
    this.getFeatureFlagsState();
    if (this.showAdsLDFlag) {
      this.fetchAdvertisementData();
    } else {
      this.hasError = true;
      this.isLoading = false;
    }
  }

  getFeatureFlagsState() {
    this.showAdsLDFlag = this.featureFlagService.snapshot[AdvertisementComponent.ADS_SET_SAIL_PASS_ENABLED];
	}

  getDomain(url: string): string {
    try {
      const hostname = new URL(url).hostname;
      return hostname;
    } catch (error) {
      return '';
    }
  }

  onAdClick() {
    this.trackAdvertisement(constants.ADVERTISEMENT.TRACKING.CLICK);
    if (this.advertisementData?.mainUrl) {
      window.open(this.advertisementData.mainUrl, '_blank');
    }
  }
  trackAdvertisement(eventType: string) {

    const internalCampaign = `${analyticsConstants.ADVERTISEMENT.INTERNAL_CAMPAIGN}:${this.advertisementData?.campaignDescription}-${this.advertisementData?.mainText}`;
    const payload: Payload = {
      interaction: analyticsConstants.ADVERTISEMENT.INTERACTION,
      currentPage: analyticsConstants.ADVERTISEMENT.CURRENT_PAGE
    }
    if(eventType === constants.ADVERTISEMENT.TRACKING.CLICK){
      this.store.dispatch(
      new tracking.TrackPageInteractionEventAction(
        {
          ...payload,
          internalCampaign
        }
      )
    );
    }

    const vdsIdCookie = jwtDecode<VdsIdCookie>(
      readCookie(constants.COOKIES.OLCI.OLCI_DATA)
    );
    const sessionCookie = vdsIdCookie.vdsId;
    const pageId = this.advertisementPageId;
    const trackingId = this.advertisementData?.actionId;

    const subscription = this.advertisementService
      .trackAdvertisement(sessionCookie, pageId, eventType, trackingId)
      .subscribe({
        next: () => {},
        error: (error) => {
          return of(error);
        }
      });
    this.subscriptions.push(subscription);
  }

  fetchAdvertisementData(): void {
    const vdsIdCookie = jwtDecode<VdsIdCookie>(
      readCookie(constants.COOKIES.OLCI.OLCI_DATA)
    );
    const sessionCookie = vdsIdCookie.vdsId;
    const pageId = this.advertisementPageId;
    const userId = vdsIdCookie.vdsId;

    const subscription = this.advertisementService
      .getAdvertisementByPageId(sessionCookie, pageId, userId)
      .subscribe({
        next: (result) => {
          if (result?.errors || !result?.data?.advertisementByPageId) {
            this.hasError = true;
          } else {
            this.advertisementData = result?.data?.advertisementByPageId;
            this.hasError = false;
          }
          this.isLoading = false;
          this.trackAdvertisement(constants.ADVERTISEMENT.TRACKING.VIEW);
        },
        error: (error) => {
          this.isLoading = false;
          this.hasError = true;
          return of(error);
        }
      });
    this.subscriptions.push(subscription);
  }
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
